import React, { useRef, useEffect, useState } from 'react';
import { motion, useInView, useReducedMotion } from 'framer-motion';
import { Building, Building2, Store } from 'lucide-react';

const OurJourney = () => {
  const prefersReducedMotion = useReducedMotion();
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { once: true, margin: "-100px" });
  const [lineProgress, setLineProgress] = useState(0);

  // Timeline milestones data
  const milestones = [
    {
      year: '2005',
      label: 'Single-Family Investments Begin',
      description: 'Launch of residential real estate investment operations.',
      icon: Building
    },
    {
      year: '2012',
      label: 'Expansion into Multifamily',
      description: 'Strategic shift into larger-scale multifamily assets.',
      icon: Building2
    },
    {
      year: '2019',
      label: 'Entry into Retail & Commercial',
      description: 'Portfolio diversification into commercial property sectors.',
      icon: Store
    }
  ];

  // Animate line drawing when in view
  useEffect(() => {
    if (isInView && !prefersReducedMotion) {
      const timer = setTimeout(() => {
        setLineProgress(100);
      }, 200);
      return () => clearTimeout(timer);
    } else if (isInView && prefersReducedMotion) {
      setLineProgress(100);
    }
  }, [isInView, prefersReducedMotion]);

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: prefersReducedMotion ? 0 : 0.15,
        delayChildren: prefersReducedMotion ? 0 : 0.3
      }
    }
  };

  const cardVariants = {
    hidden: { 
      opacity: 0, 
      y: prefersReducedMotion ? 0 : 12 
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: prefersReducedMotion ? 0 : 0.5,
        ease: [0.25, 0.46, 0.45, 0.94]
      }
    }
  };

  const headerVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: prefersReducedMotion ? 0 : 0.6,
        ease: [0.25, 0.46, 0.45, 0.94]
      }
    }
  };

  return (
    <section 
      ref={sectionRef}
      id="journey" 
      className="relative py-16 lg:py-24 overflow-hidden bg-gradient-to-b from-[#0d1a2d] via-[#0A1628] to-[#0d1a2d]"
    >
      {/* Subtle background elements */}
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(212,175,55,0.02)_0%,transparent_70%)]" />
      
      <div className="relative z-10 max-w-[1300px] mx-auto px-5 sm:px-6 lg:px-12">
        {/* Section Header */}
        <motion.div 
          className="text-center mb-12 lg:mb-16"
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
          variants={containerVariants}
        >
          <motion.span 
            className="inline-block text-[#D4AF37] text-xs sm:text-sm font-semibold tracking-wider uppercase mb-3"
            variants={headerVariants}
          >
            Our Evolution
          </motion.span>
          <motion.h2 
            className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-4"
            variants={headerVariants}
          >
            Our <span className="bg-gradient-to-r from-[#D4AF37] via-[#F5D76E] to-[#D4AF37] bg-clip-text text-transparent">Journey</span>
          </motion.h2>
          <motion.p 
            className="text-white/50 text-sm sm:text-base max-w-xl mx-auto"
            variants={headerVariants}
          >
            Two decades of strategic growth and disciplined investment across real estate sectors.
          </motion.p>
        </motion.div>

        {/* Timeline Container */}
        <motion.div 
          className="relative"
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
          variants={containerVariants}
        >
          {/* ==================== DESKTOP TIMELINE ==================== */}
          <div className="hidden md:block">
            {/* Horizontal connecting line */}
            <div className="absolute top-[60px] left-[16.66%] right-[16.66%] h-[2px] bg-white/10 rounded-full">
              <motion.div 
                className="h-full bg-gradient-to-r from-[#D4AF37] via-[#F5D76E] to-[#D4AF37] rounded-full origin-left"
                style={{
                  width: `${lineProgress}%`,
                  transition: prefersReducedMotion ? 'none' : 'width 0.8s ease-out'
                }}
              />
            </div>

            {/* Milestone Cards Grid */}
            <div className="grid grid-cols-3 gap-6 lg:gap-8">
              {milestones.map((milestone, index) => (
                <motion.div
                  key={milestone.year}
                  variants={cardVariants}
                  className="relative"
                >
                  {/* Dot on timeline */}
                  <div className="flex justify-center mb-6">
                    <motion.div 
                      className="w-4 h-4 rounded-full bg-[#D4AF37] border-4 border-[#0A1628] shadow-lg shadow-[#D4AF37]/30 z-10"
                      initial={{ scale: 0 }}
                      animate={isInView ? { scale: 1 } : { scale: 0 }}
                      transition={{ 
                        delay: prefersReducedMotion ? 0 : 0.4 + index * 0.15,
                        duration: prefersReducedMotion ? 0 : 0.3,
                        ease: "easeOut"
                      }}
                    />
                  </div>

                  {/* Card */}
                  <motion.div
                    className="relative rounded-2xl p-6 lg:p-7 border border-white/[0.08] group cursor-default"
                    style={{
                      background: 'rgba(17, 29, 53, 0.5)',
                      backdropFilter: 'blur(12px)',
                      boxShadow: '0 4px 24px rgba(0, 0, 0, 0.15)'
                    }}
                    whileHover={!prefersReducedMotion ? { 
                      y: -4,
                      borderColor: 'rgba(212, 175, 55, 0.2)',
                      boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2), 0 0 20px rgba(212, 175, 55, 0.05)'
                    } : {}}
                    transition={{ duration: 0.3 }}
                  >
                    {/* Inner glow on hover */}
                    <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-[#D4AF37]/[0.02] to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none" />
                    
                    {/* Year */}
                    <div className="mb-3">
                      <span className="text-3xl lg:text-4xl font-bold bg-gradient-to-r from-[#D4AF37] via-[#F5D76E] to-[#D4AF37] bg-clip-text text-transparent group-hover:from-[#F5D76E] group-hover:via-[#D4AF37] group-hover:to-[#F5D76E] transition-all duration-300">
                        {milestone.year}
                      </span>
                    </div>

                    {/* Icon + Title */}
                    <div className="flex items-center gap-2 mb-2">
                      <milestone.icon className="w-4 h-4 text-[#D4AF37]/70" />
                      <h3 className="text-white font-semibold text-sm lg:text-base leading-tight">
                        {milestone.label}
                      </h3>
                    </div>

                    {/* Description */}
                    <p className="text-white/50 text-xs lg:text-sm leading-relaxed">
                      {milestone.description}
                    </p>
                  </motion.div>
                </motion.div>
              ))}
            </div>
          </div>

          {/* ==================== MOBILE/TABLET VERTICAL TIMELINE ==================== */}
          <div className="md:hidden">
            <div className="relative pl-8">
              {/* Vertical connecting line */}
              <div className="absolute left-[7px] top-4 bottom-4 w-[2px] bg-white/10 rounded-full">
                <motion.div 
                  className="w-full bg-gradient-to-b from-[#D4AF37] via-[#F5D76E] to-[#D4AF37] rounded-full origin-top"
                  style={{
                    height: `${lineProgress}%`,
                    transition: prefersReducedMotion ? 'none' : 'height 0.8s ease-out'
                  }}
                />
              </div>

              {/* Milestone Cards */}
              <div className="space-y-6">
                {milestones.map((milestone, index) => (
                  <motion.div
                    key={milestone.year}
                    variants={cardVariants}
                    className="relative"
                  >
                    {/* Dot on timeline */}
                    <motion.div 
                      className="absolute left-[-25px] top-6 w-4 h-4 rounded-full bg-[#D4AF37] border-4 border-[#0A1628] shadow-lg shadow-[#D4AF37]/30 z-10"
                      initial={{ scale: 0 }}
                      animate={isInView ? { scale: 1 } : { scale: 0 }}
                      transition={{ 
                        delay: prefersReducedMotion ? 0 : 0.3 + index * 0.15,
                        duration: prefersReducedMotion ? 0 : 0.3,
                        ease: "easeOut"
                      }}
                    />

                    {/* Card */}
                    <div
                      className="rounded-2xl p-5 border border-white/[0.08]"
                      style={{
                        background: 'rgba(17, 29, 53, 0.5)',
                        backdropFilter: 'blur(12px)',
                        boxShadow: '0 4px 24px rgba(0, 0, 0, 0.15)'
                      }}
                    >
                      {/* Year */}
                      <div className="mb-2">
                        <span className="text-2xl font-bold bg-gradient-to-r from-[#D4AF37] via-[#F5D76E] to-[#D4AF37] bg-clip-text text-transparent">
                          {milestone.year}
                        </span>
                      </div>

                      {/* Icon + Title */}
                      <div className="flex items-center gap-2 mb-2">
                        <milestone.icon className="w-4 h-4 text-[#D4AF37]/70" />
                        <h3 className="text-white font-semibold text-sm leading-tight">
                          {milestone.label}
                        </h3>
                      </div>

                      {/* Description */}
                      <p className="text-white/50 text-xs leading-relaxed">
                        {milestone.description}
                      </p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default OurJourney;
